import React, { useState, useEffect, useMemo } from 'react';
import { useErrorModal } from './contexts/ErrorNotificationContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  Tab,
  Tabs,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  Divider,
  LinearProgress,
  Stack,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import HistoryIcon from '@mui/icons-material/History';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ShieldIcon from '@mui/icons-material/Shield';
import { useAuth } from './contexts/AuthContext';
import api from './services/api';

// Inline password strength indicator
const PASSWORD_RULES = [
  { label: 'At least 10 characters', test: (p: string) => p.length >= 10 },
  { label: 'Uppercase letter', test: (p: string) => /[A-Z]/.test(p) },
  { label: 'Lowercase letter', test: (p: string) => /[a-z]/.test(p) },
  { label: 'A digit', test: (p: string) => /\d/.test(p) },
  { label: 'Special character', test: (p: string) => /[^A-Za-z0-9]/.test(p) },
];

const PasswordStrengthIndicator: React.FC<{ password: string }> = ({ password }) => {
  const passed = PASSWORD_RULES.filter((r) => r.test(password)).length;
  const pct = (passed / PASSWORD_RULES.length) * 100;
  const color = pct <= 40 ? '#e74c3c' : pct <= 80 ? '#f39c12' : '#2ecc71';

  if (!password) return null;

  return (
    <Box sx={{ mt: 1, mb: 0.5 }}>
      <LinearProgress
        variant="determinate"
        value={pct}
        sx={{
          height: 6,
          borderRadius: 3,
          backgroundColor: 'rgba(0,0,0,0.08)',
          '& .MuiLinearProgress-bar': { backgroundColor: color, borderRadius: 3 },
        }}
      />
      <Box sx={{ mt: 0.5 }}>
        {PASSWORD_RULES.map((rule) => {
          const ok = rule.test(password);
          return (
            <Box key={rule.label} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {ok ? (
                <CheckIcon sx={{ fontSize: 16, color: '#2ecc71' }} />
              ) : (
                <CloseIcon sx={{ fontSize: 16, color: '#ccc' }} />
              )}
              <Typography variant="caption" sx={{ color: ok ? '#2ecc71' : '#999' }}>
                {rule.label}
              </Typography>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

const FEATURES = [
  { icon: <ShowChartIcon sx={{ color: '#22d3ee' }} />, title: 'Strategy Builder', desc: 'Design custom trading strategies visually' },
  { icon: <HistoryIcon sx={{ color: '#34d399' }} />, title: 'Backtesting', desc: 'Test against years of historical data' },
  { icon: <TrendingUpIcon sx={{ color: '#E8A045' }} />, title: 'Live Trading', desc: 'Execute with confidence in real time' },
  { icon: <ShieldIcon sx={{ color: '#a78bfa' }} />, title: 'Risk Management', desc: 'Built-in safeguards for every trade' },
];

export default function Login() {
  const navigate = useNavigate();
  const { login, register } = useAuth();

  const [tabValue, setTabValue] = useState(0);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [tosAccepted, setTosAccepted] = useState(false);
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [error, setError] = useErrorModal();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [oauthProviders, setOauthProviders] = useState<Record<string, boolean>>({});
  const allPasswordRulesPassed = useMemo(
    () => PASSWORD_RULES.every((r) => r.test(password)),
    [password]
  );
  const [searchParams] = useSearchParams();
  const [showForgot, setShowForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotSent, setForgotSent] = useState(false);

  useEffect(() => {
    const oauthError = searchParams.get('oauth_error');
    if (oauthError) setError(decodeURIComponent(oauthError));

    const tab = searchParams.get('tab');
    if (tab === 'register') setTabValue(1);

    api.get('/api/v1/auth/oauth/providers')
      .then((res) => setOauthProviders(res.data))
      .catch(() => {});
  }, [searchParams]);

  const resetForm = () => {
    setEmail('');
    setPassword('');
    setDisplayName('');
    setTosAccepted(false);
    setPrivacyAccepted(false);
    setError('');
  };

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    resetForm();
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);

    try {
      await login(email, password);
      navigate('/dashboard');
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Login failed. Please check your credentials.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!tosAccepted) {
      setError('You must accept the Terms of Service to continue.');
      return;
    }
    if (!privacyAccepted) {
      setError('You must accept the Privacy Policy to continue.');
      return;
    }

    setIsSubmitting(true);

    try {
      await register(email, password, displayName);
      navigate('/dashboard');
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Registration failed. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
      }}
    >
      {/* Left panel — Brand & Features */}
      <Box
        sx={{
          flex: { md: 1 },
          background: 'linear-gradient(135deg, #1a3a3a 0%, #3A8C8C 50%, #2a6e5a 100%)',
          color: '#fff',
          display: { xs: 'none', md: 'flex' },
          flexDirection: 'column',
          justifyContent: 'center',
          p: 6,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Decorative gradient orbs */}
        <Box sx={{
          position: 'absolute', top: -100, right: -100, width: 300, height: 300,
          borderRadius: '50%', background: 'radial-gradient(circle, rgba(232,160,69,0.15) 0%, transparent 70%)',
        }} />
        <Box sx={{
          position: 'absolute', bottom: -80, left: -80, width: 250, height: 250,
          borderRadius: '50%', background: 'radial-gradient(circle, rgba(167,139,250,0.15) 0%, transparent 70%)',
        }} />

        <Box sx={{ position: 'relative', zIndex: 1, maxWidth: 480 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 4 }}>
            <Box
              sx={{
                width: 48, height: 48, borderRadius: 2.5,
                background: 'linear-gradient(135deg, #3A8C8C, #E8A045)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}
            >
              <ShowChartIcon sx={{ color: '#fff', fontSize: 26 }} />
            </Box>
            <Typography variant="h5" sx={{ fontWeight: 800, letterSpacing: '-0.02em', fontFamily: '"Fraunces", serif' }}>
              Quantato
            </Typography>
          </Box>

          <Typography variant="h3" sx={{ fontWeight: 800, mb: 2, lineHeight: 1.15, letterSpacing: '-0.03em', fontFamily: '"Fraunces", serif' }}>
            Build, test, and
            <br />
            <Box component="span" sx={{ background: 'linear-gradient(90deg, #E8A045, #fbbf24)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
              trade smarter.
            </Box>
          </Typography>

          <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '1.1rem', mb: 5, lineHeight: 1.6 }}>
            Design custom strategies, backtest against real data, and trade with confidence. Your edge in the prediction markets.
          </Typography>

          <Stack spacing={2.5}>
            {FEATURES.map((f) => (
              <Box key={f.title} sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                <Box sx={{
                  width: 40, height: 40, borderRadius: 2,
                  bgcolor: 'rgba(255,255,255,0.08)', display: 'flex',
                  alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                }}>
                  {f.icon}
                </Box>
                <Box>
                  <Typography sx={{ fontWeight: 600, fontSize: '0.95rem' }}>{f.title}</Typography>
                  <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.85rem' }}>{f.desc}</Typography>
                </Box>
              </Box>
            ))}
          </Stack>
        </Box>
      </Box>

      {/* Right panel — Login Form */}
      <Box
        sx={{
          flex: { md: 1 },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(135deg, #FDF6EC 0%, #F5E6D0 50%, #FDF6EC 100%)',
          p: { xs: 3, md: 6 },
          minHeight: { xs: '100vh', md: 'auto' },
        }}
      >
        <Box sx={{ maxWidth: 440, width: '100%' }}>
          {/* Mobile logo */}
          <Box sx={{ display: { xs: 'flex', md: 'none' }, flexDirection: 'column', alignItems: 'center', mb: 3 }}>
            <Typography
              component="h1" variant="h3" gutterBottom
              sx={{ fontWeight: 700, color: '#3A8C8C', textAlign: 'center', mb: 1 }}
            >
              Quantato
            </Typography>
            <Typography variant="subtitle1" sx={{ color: '#7A5F4E', textAlign: 'center' }}>
              Build, test, and trade your strategies.
            </Typography>
          </Box>

          <Paper
            elevation={8}
            sx={{
              p: 4,
              width: '100%',
              backgroundColor: '#FFFFFF',
              borderRadius: 3,
              border: '1px solid rgba(58, 140, 140, 0.2)',
            }}
          >
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              centered
              sx={{
                mb: 2,
                '& .MuiTab-root': { fontWeight: 600, fontSize: '1rem' },
              }}
            >
              <Tab label="Login" />
              <Tab label="Register" />
            </Tabs>

            {/* Error display handled by ErrorNotificationContext modal */}

            {/* Login Form */}
            {tabValue === 0 && !showForgot && (
              <Box component="form" onSubmit={handleLogin} sx={{ mt: 2 }}>
                <TextField
                  id="login-email" margin="normal" required fullWidth
                  label="Email" type="email" value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="email" autoFocus
                  InputLabelProps={{ htmlFor: 'login-email' }}
                  error={!!error}
                  aria-describedby={error ? 'login-error' : undefined}
                  aria-invalid={!!error}
                />
                <TextField
                  id="login-password" margin="normal" required fullWidth
                  label="Password" type="password" value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                  InputLabelProps={{ htmlFor: 'login-password' }}
                  error={!!error}
                  aria-describedby={error ? 'login-error' : undefined}
                  aria-invalid={!!error}
                />
                <Box sx={{ textAlign: 'right', mt: 0.5 }}>
                  <Button
                    size="small"
                    sx={{ color: '#7A5F4E', textTransform: 'none', '&:hover': { color: '#3A8C8C' } }}
                    onClick={() => { setShowForgot(true); setError(''); setForgotSent(false); setForgotEmail(email); }}
                  >
                    Forgot password?
                  </Button>
                </Box>
                <Button
                  type="submit" fullWidth variant="contained" disabled={isSubmitting}
                  sx={{ mt: 2, mb: 2, py: 1.5, fontWeight: 600, fontSize: '1rem' }}
                >
                  {isSubmitting ? <CircularProgress size={24} color="inherit" /> : 'Sign In'}
                </Button>
              </Box>
            )}

            {/* Forgot Password Form */}
            {tabValue === 0 && showForgot && (
              <Box sx={{ mt: 2 }}>
                {forgotSent ? (
                  <Alert severity="success" sx={{ mb: 2 }}>
                    If that email is registered, we&apos;ve sent a reset link. Check your inbox.
                  </Alert>
                ) : (
                  <>
                    <Typography variant="body2" sx={{ color: '#7A5F4E', mb: 2 }}>
                      Enter your email and we&apos;ll send you a link to reset your password.
                    </Typography>
                    <TextField
                      id="forgot-email" margin="normal" required fullWidth
                      label="Email" type="email" value={forgotEmail}
                      onChange={(e) => setForgotEmail(e.target.value)}
                      autoFocus
                      InputLabelProps={{ htmlFor: 'forgot-email' }}
                    />
                    <Button
                      fullWidth variant="contained"
                      disabled={isSubmitting || !forgotEmail}
                      sx={{ mt: 2, mb: 2, py: 1.5, fontWeight: 600 }}
                      onClick={async () => {
                        setIsSubmitting(true);
                        setError('');
                        try {
                          await api.post('/api/v1/auth/forgot-password', { email: forgotEmail });
                          setForgotSent(true);
                        } catch (err: any) {
                          setError(err.response?.data?.detail || 'Something went wrong. Please try again.');
                        } finally {
                          setIsSubmitting(false);
                        }
                      }}
                    >
                      {isSubmitting ? <CircularProgress size={24} color="inherit" /> : 'Send Reset Link'}
                    </Button>
                  </>
                )}
                <Button
                  size="small"
                  sx={{ color: '#7A5F4E', textTransform: 'none', '&:hover': { color: '#3A8C8C' } }}
                  onClick={() => { setShowForgot(false); setError(''); }}
                >
                  Back to login
                </Button>
              </Box>
            )}

            {/* Register Form */}
            {tabValue === 1 && (
              <Box component="form" onSubmit={handleRegister} sx={{ mt: 2 }}>
                <TextField
                  id="register-display-name" margin="normal" required fullWidth
                  label="Display Name" value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  autoFocus
                  InputLabelProps={{ htmlFor: 'register-display-name' }}
                  error={!!error}
                  aria-describedby={error ? 'login-error' : undefined}
                  aria-invalid={!!error}
                />
                <TextField
                  id="register-email" margin="normal" required fullWidth
                  label="Email" type="email" value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="email"
                  InputLabelProps={{ htmlFor: 'register-email' }}
                  error={!!error}
                  aria-describedby={error ? 'login-error' : undefined}
                  aria-invalid={!!error}
                />
                <TextField
                  id="register-password" margin="normal" required fullWidth
                  label="Password" type="password" value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="new-password"
                  InputLabelProps={{ htmlFor: 'register-password' }}
                  error={!!error}
                  aria-describedby={error ? 'login-error' : undefined}
                  aria-invalid={!!error}
                />
                <PasswordStrengthIndicator password={password} />
                <FormControlLabel
                  htmlFor="register-tos"
                  control={
                    <Checkbox
                      id="register-tos" checked={tosAccepted}
                      onChange={(e) => setTosAccepted(e.target.checked)}
                      color="primary"
                    />
                  }
                  label={
                    <Typography variant="body2" sx={{ color: '#7A5F4E' }}>
                      I agree to the Terms of Service
                    </Typography>
                  }
                  sx={{ mt: 1 }}
                />
                <FormControlLabel
                  htmlFor="register-privacy"
                  control={
                    <Checkbox
                      id="register-privacy" checked={privacyAccepted}
                      onChange={(e) => setPrivacyAccepted(e.target.checked)}
                      color="primary"
                    />
                  }
                  label={
                    <Typography variant="body2" sx={{ color: '#7A5F4E' }}>
                      I agree to the Privacy Policy
                    </Typography>
                  }
                />
                <Button
                  type="submit" fullWidth variant="contained"
                  disabled={isSubmitting || !allPasswordRulesPassed}
                  sx={{ mt: 3, mb: 2, py: 1.5, fontWeight: 600, fontSize: '1rem' }}
                >
                  {isSubmitting ? <CircularProgress size={24} color="inherit" /> : 'Create Account'}
                </Button>
              </Box>
            )}

            {/* Social Login Buttons */}
            {(oauthProviders.google || oauthProviders.apple || oauthProviders.amazon) && (
              <>
                <Divider sx={{ my: 3, color: '#7A5F4E' }}>
                  <Typography variant="caption" sx={{ color: '#7A5F4E', px: 1 }}>
                    OR
                  </Typography>
                </Divider>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {oauthProviders.google && (
                    <Button
                      fullWidth variant="outlined"
                      onClick={() => { window.location.href = '/api/v1/auth/oauth/google'; }}
                      sx={{
                        py: 1.2, borderColor: '#F0E4CE', color: '#4A3728',
                        textTransform: 'none', fontSize: '0.95rem', fontWeight: 500,
                        '&:hover': { borderColor: '#4285f4', backgroundColor: 'rgba(66,133,244,0.08)' },
                      }}
                    >
                      <Box component="img" src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" sx={{ width: 20, height: 20, mr: 1.5 }} />
                      Continue with Google
                    </Button>
                  )}

                  {oauthProviders.apple && (
                    <Button
                      fullWidth variant="outlined"
                      onClick={() => { window.location.href = '/api/v1/auth/oauth/apple'; }}
                      sx={{
                        py: 1.2, borderColor: '#F0E4CE', color: '#4A3728',
                        textTransform: 'none', fontSize: '0.95rem', fontWeight: 500,
                        '&:hover': { borderColor: '#3A8C8C', backgroundColor: '#F0E4CE' },
                      }}
                    >
                      <Box component="span" sx={{ mr: 1.5, fontSize: '1.2rem', lineHeight: 1 }}></Box>
                      Continue with Apple
                    </Button>
                  )}

                  {oauthProviders.amazon && (
                    <Button
                      fullWidth variant="outlined"
                      onClick={() => { window.location.href = '/api/v1/auth/oauth/amazon'; }}
                      sx={{
                        py: 1.2, borderColor: '#F0E4CE', color: '#4A3728',
                        textTransform: 'none', fontSize: '0.95rem', fontWeight: 500,
                        '&:hover': { borderColor: '#ff9900', backgroundColor: 'rgba(255,153,0,0.08)' },
                      }}
                    >
                      <Box component="span" sx={{ mr: 1.5, fontSize: '1.2rem', lineHeight: 1, color: '#ff9900' }}>a</Box>
                      Continue with Amazon
                    </Button>
                  )}
                </Box>
              </>
            )}
          </Paper>
        </Box>
      </Box>
    </Box>
  );
}
